import type { GradeMappingSchema } from '@repo/schemas/grade-scale/gradeScale';

import type { GradeBand } from './gradeScale.types';

import { getMappingBands } from './gradeScaleBands';
import { gradeScalePalette } from './gradeScaleColors';

export interface ResolvedGrade {
  /** Mapped band label, or null when nothing resolves. */
  label: string | null;
  /** Band color from the destructive → success ramp, or null when unmapped. */
  color: string | null;
}

const UNRESOLVED: ResolvedGrade = { label: null, color: null };

function bandAt(bands: GradeBand[], index: number): ResolvedGrade {
  const band = bands[index];
  const color = gradeScalePalette(bands.length)[index];
  if (!band || !color) return UNRESOLVED;
  return {
    label: band.name,
    color,
  };
}

/**
 * Label match only — a numeric label like '5' is the band named '5', never
 * the 5% position, so a percentage can't shadow it and vice versa.
 */
export function resolveGradeLabel(mapping: GradeMappingSchema | null | undefined, label: string): ResolvedGrade {
  const bands = getMappingBands(mapping);
  if (!bands) return UNRESOLVED;

  const index = bands.findIndex((band) => band.name === label);
  return index === -1 ? UNRESOLVED : bandAt(bands, index);
}

/** Range match only into the band containing the percentage (bands partition 0-100). */
export function resolveGradePercent(mapping: GradeMappingSchema | null | undefined, percent: number): ResolvedGrade {
  const bands = getMappingBands(mapping);
  if (!bands || !Number.isFinite(percent)) return UNRESOLVED;

  const clamped = Math.min(Math.max(percent, 0), 100);
  const index = bands.findIndex((band) => clamped >= band.minPercent && clamped <= band.maxPercent);
  return index === -1 ? UNRESOLVED : bandAt(bands, index);
}
